import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box } from "@mui/material";
import { Wheel } from "react-custom-roulette";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:3001";

const WHEEL_PX = 445;

const mediaUrl = (url) => {
  if (!url) return "";
  const value = String(url).trim();
  if (!value) return "";
  if (/^https?:\/\//i.test(value) || value.startsWith("data:") || value.startsWith("blob:")) {
    return value;
  }
  // Public files such as /mind_your_music.png are served by the client, not the API.
  if (value.startsWith("/") && !value.startsWith("/uploads") && !value.startsWith("/api/")) {
    return value;
  }
  return `${API_BASE}${value.startsWith("/") ? value : `/${value}`}`;
};

const rotateImageUri = (uri, degrees) =>
  new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const canvas = document.createElement("canvas");
      const size = Math.max(img.width, img.height);
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext("2d");
      ctx.translate(size / 2, size / 2);
      ctx.rotate((degrees * Math.PI) / 180);
      ctx.drawImage(img, -img.width / 2, -img.height / 2);
      resolve(canvas.toDataURL("image/png"));
    };
    img.onerror = () => resolve(uri);
    img.src = uri;
  });

const toWheelData = (slices = []) =>
  slices.map((slice) => {
    const wd = {
      option: (slice.text ?? slice.display_name ?? "").trim(),
      style: {
        backgroundColor: slice.color || slice.slice_color || "#3e3e3e",
        textColor: "#fff",
      },
    };
    const img = slice.background_image_url || slice.image_url;
    if (img) {
      wd.image = {
        uri: mediaUrl(img),
        sizeMultiplier: slice.image_zoom ?? 0.6,
        offsetX: slice.image_offset_x ?? 0,
        offsetY: slice.image_offset_y ?? 0,
      };
    }
    return wd;
  });

// New array identities make react-custom-roulette reload every slice image
// and hide the wheel until they finish. Keep these referentially stable.
const WHEEL_BACKGROUND_COLORS = ["#3e3e3e", "#df3428"];
const WHEEL_TEXT_COLORS = ["#ffffff"];
const MemoWheel = React.memo(Wheel);
const noop = () => {};

// Slow wind-up, fast middle, long crawl onto the winning slice.
export const SUSPENSE_SPIN_MS = 8400;

/** Pointer offset matches react-custom-roulette so the slice stops under its arrow. */
const landingDegrees = (prizeNumber, count) => {
  const prize = Math.max(0, Math.min(Number(prizeNumber) || 0, count - 1));
  const degreesPerPrize = 360 / count;
  const initialRotation = 43 + degreesPerPrize / 2;
  const randomDifference = (-1 + Math.random() * 2) * degreesPerPrize * 0.35;
  const prizeRotation =
    degreesPerPrize * (count - prize) - initialRotation + randomDifference;
  return count - prize > count / 2 ? -360 + prizeRotation : prizeRotation;
};

const normalizeDegrees = (deg) => {
  const mod = Number(deg) % 360;
  return mod < 0 ? mod + 360 : mod;
};

const readRotationDegrees = (el) => {
  const tr = window.getComputedStyle(el).transform;
  if (!tr || tr === "none") return 0;
  try {
    const m = new DOMMatrix(tr);
    return normalizeDegrees(Math.atan2(m.b, m.a) * (180 / Math.PI));
  } catch {
    return 0;
  }
};

export const sliceSignature = (slices = []) =>
  slices
    .map((slice) =>
      [
        slice.text ?? slice.display_name ?? "",
        slice.color ?? slice.slice_color ?? "",
        slice.background_image_url || "",
        slice.image_url || "",
        slice.image_zoom ?? "",
        slice.image_rotation ?? "",
        slice.image_offset_x ?? "",
        slice.image_offset_y ?? "",
      ].join("|")
    )
    .join(";");

const sameSliceId = (entry, winnerId) => {
  if (winnerId == null || winnerId === '') return false;
  const target = Number(winnerId);
  if (!Number.isFinite(target)) return false;
  return Number(entry?.id) === target || Number(entry?.slice_id) === target;
};

export const prizeIndexFromSpin = (winnerIdx, winnerId, entries = []) => {
  const idx = Number(winnerIdx);
  if (Number.isInteger(idx) && idx >= 0 && idx < entries.length) return idx;
  const found = entries.findIndex((entry) => sameSliceId(entry, winnerId));
  return found >= 0 ? found : 0;
};

export const entryFromSpin = (winnerIdx, winnerId, entries = []) => {
  const match = entries.find((entry) => sameSliceId(entry, winnerId));
  if (match) return match;
  const idx = Number(winnerIdx);
  if (Number.isInteger(idx) && idx >= 0 && idx < entries.length) return entries[idx];
  return null;
};

/**
 * The solo / default prize wheel (react-custom-roulette) with the same
 * borders, pointer, center logo, and slice image placement.
 * `maxSize` draws the wheel at that pixel size, smaller or larger than the base wheel.
 */
const DefaultStyleWheel = ({
  slices = [],
  logoUrl = "/mind_your_music.png",
  mustStartSpinning = false,
  prizeNumber = 0,
  onStopSpinning,
  maxSize = 0,
  spinning = false,
  suspenseSpin = false,
}) => {
  const pendingRef = useRef(null);
  const spinningRef = useRef(mustStartSpinning);
  const slicesRef = useRef(slices);
  const faceRef = useRef(null);
  const onStopRef = useRef(onStopSpinning);
  const prizeNumberRef = useRef(prizeNumber);
  const [data, setData] = useState([]);
  const [restIndex, setRestIndex] = useState(-1);
  const signature = useMemo(() => sliceSignature(slices), [slices]);

  spinningRef.current = mustStartSpinning;
  slicesRef.current = slices;
  onStopRef.current = onStopSpinning;
  prizeNumberRef.current = prizeNumber;

  useEffect(() => {
    let cancelled = false;
    const currentSlices = slicesRef.current;
    const sync = toWheelData(currentSlices);
    const jobs = [];
    currentSlices.forEach((slice, idx) => {
      const rotation = slice.image_rotation ?? 0;
      const img = slice.background_image_url || slice.image_url;
      if (!img || !rotation) return;
      jobs.push(
        rotateImageUri(mediaUrl(img), rotation).then((uri) => ({ idx, uri }))
      );
    });
    const publish = (next) => {
      if (cancelled) return;
      if (spinningRef.current) pendingRef.current = next;
      else setData(next);
    };
    if (!jobs.length) {
      publish(sync);
      return () => {
        cancelled = true;
      };
    }
    Promise.all(jobs)
      .then((results) => {
        const next = sync.map((item, idx) => {
          const rotated = results.find((job) => job.idx === idx);
          if (!rotated || !item.image) return item;
          return { ...item, image: { ...item.image, uri: rotated.uri } };
        });
        publish(next);
      })
      .catch(() => publish(sync));
    return () => {
      cancelled = true;
    };
  }, [signature]);

  useEffect(() => {
    if (!mustStartSpinning && pendingRef.current) {
      setData(pendingRef.current);
      pendingRef.current = null;
    }
  }, [mustStartSpinning]);

  const handleStop = useCallback(() => {
    const landed = prizeNumberRef.current;
    setRestIndex(Number.isInteger(landed) ? landed : 0);
    onStopRef.current?.();
  }, []);

  // Own the spin so the slice images stay painted, and so the wheel can
  // ease in, run fast, then crawl onto the winning slice.
  useEffect(() => {
    if (!suspenseSpin || !mustStartSpinning) return undefined;
    const count = slicesRef.current.length;
    let cancelled = false;
    let retryTimer = 0;
    let spinAnim = null;

    const begin = (attempt) => {
      if (cancelled) return;
      const rotator = faceRef.current?.querySelector("canvas")?.parentElement;
      if (!rotator || count < 2) {
        if (attempt < 8) retryTimer = window.setTimeout(() => begin(attempt + 1), 40);
        return;
      }

      const start = readRotationDegrees(rotator);
      const target = normalizeDegrees(landingDegrees(prizeNumber, count));
      let sweep = target - start;
      if (sweep < 0) sweep += 360;
      const end = start + 360 * 5 + sweep;
      const distance = end - start;
      const slow = start + distance * 0.05;
      const fast = start + distance * 0.7;

      rotator.getAnimations().forEach((anim) => anim.cancel());
      rotator.style.transformOrigin = "50% 50%";
      rotator.style.transform = `rotate(${start}deg)`;

      spinAnim = rotator.animate(
        [
          {
            transform: `rotate(${start}deg)`,
            easing: "cubic-bezier(0.55, 0.02, 0.82, 0.45)",
          },
          { transform: `rotate(${slow}deg)`, offset: 0.18, easing: "linear" },
          {
            transform: `rotate(${fast}deg)`,
            offset: 0.42,
            easing: "cubic-bezier(0.06, 0.82, 0.18, 1)",
          },
          { transform: `rotate(${end}deg)`, offset: 1 },
        ],
        { duration: SUSPENSE_SPIN_MS, fill: "forwards" }
      );

      spinAnim.finished
        .then(() => {
          if (cancelled) return;
          rotator.style.transform = `rotate(${end}deg)`;
          onStopRef.current?.();
        })
        .catch(() => {});
    };

    begin(0);

    return () => {
      cancelled = true;
      window.clearTimeout(retryTimer);
      if (spinAnim && spinAnim.playState !== "finished") spinAnim.cancel();
    };
  }, [suspenseSpin, mustStartSpinning, prizeNumber]);

  const frame = maxSize > 0 ? maxSize : 0;
  const scale = frame > 0 ? frame / WHEEL_PX : 1;
  const logo = mediaUrl(logoUrl) || "/mind_your_music.png";
  // Inner hole is 20% of the wheel radius. Cover it so slice art cannot sit on the logo.
  const logoSize = Math.round(WHEEL_PX * 0.22);

  if (!data.length) {
    const hold = frame || Math.min(WHEEL_PX, 320);
    return (
      <Box
        sx={{
          width: hold,
          height: hold,
          mx: "auto",
          borderRadius: "50%",
          bgcolor: "#14120f",
          border: "8px solid #FFD700",
          boxSizing: "border-box",
        }}
      />
    );
  }

  const face = (
    <Box
      ref={faceRef}
      sx={{
        position: "relative",
        display: "inline-flex",
        transform: maxSize
          ? "none"
          : { xs: "scale(0.72)", sm: "scale(0.88)", md: "scale(1)" },
        transformOrigin: "center",
        filter: spinning
          ? "drop-shadow(0 0 28px rgba(255,193,7,0.45))"
          : "drop-shadow(0 12px 32px rgba(0,0,0,0.45))",
        transition: "filter 0.4s ease",
      }}
    >
      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          "& > div": maxSize > 0
            ? {
                width: `${WHEEL_PX}px !important`,
                height: `${WHEEL_PX}px !important`,
                maxWidth: "none !important",
                maxHeight: "none !important",
              }
            : {},
        }}
      >
        <MemoWheel
          mustStartSpinning={suspenseSpin ? false : mustStartSpinning}
          prizeNumber={suspenseSpin ? 0 : prizeNumber}
          data={data}
          onStopSpinning={suspenseSpin ? noop : handleStop}
          backgroundColors={WHEEL_BACKGROUND_COLORS}
          textColors={WHEEL_TEXT_COLORS}
          outerBorderColor="#FFD700"
          outerBorderWidth={10}
          innerRadius={20}
          innerBorderColor="#1a1a1a"
          innerBorderWidth={8}
          radiusLineColor="rgba(0,0,0,0.4)"
          radiusLineWidth={2}
          fontSize={13}
          textDistance={58}
          spinDuration={0.55}
          startingOptionIndex={suspenseSpin ? -1 : restIndex}
          disableInitialAnimation
        />
      </Box>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: logoSize,
          height: logoSize,
          borderRadius: "50%",
          backgroundColor: "#000",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 20,
          border: "3px solid #FFD700",
          overflow: "hidden",
          pointerEvents: "none",
          boxShadow: "0 0 18px rgba(255,193,7,0.35)",
        }}
      >
        <Box
          component="img"
          src={logo}
          alt="Logo"
          sx={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
      </Box>
    </Box>
  );

  if (!maxSize) return face;

  return (
    <Box
      sx={{
        width: frame,
        height: frame,
        position: "relative",
        mx: "auto",
        flexShrink: 0,
        overflow: "hidden",
        bgcolor: "#14120f",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          left: "50%",
          top: "50%",
          transform: `translate(-50%, -50%) scale(${scale})`,
        }}
      >
        {face}
      </Box>
    </Box>
  );
};

export default DefaultStyleWheel;
